import { useMemo, useState } from "react";
import { Router, Pencil, Plus, Search, Trash2, Circle, ShieldCheck, DownloadCloud } from "lucide-react";
import {
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/EmptyState";
import { useDebounce } from "@/hooks/useDebounce";
import { formatDate } from "@/lib/utils";
import { ReconcileResultDialog, type ReconcileResult } from "@/components/ReconcileResultDialog";
import type { ApiError } from "@/lib/api";
import type { RouterSummary, EstatusRouter } from "./schemas";
import {
  useDeleteRouter, useListRouters, useReconciliarTodo, useRecrearReglas, useRouter,
} from "./api";
import { RouterFormDialog } from "./RouterFormDialog";
import { toast } from "sonner";
import { useJobPolling } from "@/features/jobs/useJobPolling";
// rc140 — móvil: tarjetas
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileCard, MobileEmpty, MobileList, MobileMeta } from "@/components/ui/mobile-list";

const ESTATUS_COLOR: Record<EstatusRouter, string> = {
  online: "text-emerald-500",
  offline: "text-muted-foreground",
  unknown: "text-muted-foreground",
  error: "text-destructive",
};

export function RoutersListPage() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 300);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { data: editing } = useRouter(editingId);

  const filters = useMemo(() => ({ page, size: 20, search }), [page, search]);
  const { data, isLoading, isFetching } = useListRouters(filters);
  const deleteMut = useDeleteRouter();
  const recrearMut = useRecrearReglas();
  const reconciliarTodoMut = useReconciliarTodo();
  const [reconcileResult, setReconcileResult] = useState<ReconcileResult | null>(null);
  const [reconcileTitle, setReconcileTitle] = useState<string>("Resultado");
  const [pollingJobId, setPollingJobId] = useState<string | null>(null);

  useJobPolling(pollingJobId, {
    invalidate: [["contratos"], ["clientes", "resumen"], ["mikrotik-jobs"]],
    onComplete: (job) => {
      setPollingJobId(null);
      const res = (job.result ?? {}) as ReconcileResult;
      setReconcileResult({ ...res, success: true });
      toast.success("Operación completada", {
        description: res.anomalies?.length
          ? `${res.anomalies.length} anomalía(s) reportadas`
          : "Router consistente",
      });
    },
    onError: (job) => {
      setPollingJobId(null);
      setReconcileResult({
        success: false,
        action: job.action,
        anomalies: [],
        error: job.error ?? "Error desconocido",
      });
      toast.error("Operación falló", { description: job.error ?? "Ver detalles" });
    },
  });
  const items = data?.items ?? [];
  const totalPages = data?.pages ?? 1;

  async function handleDelete(r: RouterSummary) {
    if (!confirm(`¿Eliminar el router "${r.nombre}" (${r.ip_address})?\nNo debe tener contratos activos ligados.`)) return;
    try { await deleteMut.mutateAsync(r.id); }
    catch (e) { alert(`Error: ${(e as { message?: string })?.message}`); }
  }

  async function handleRecrearReglas(r: RouterSummary) {
    setReconcileTitle("Recrear reglas de firewall");
    try {
      const res = await recrearMut.mutateAsync(r.id);
      setPollingJobId(res.job_id);
      toast.info("Operación encolada", { description: "Recreando reglas en el router…" });
    } catch (e) {
      toast.error("Error al encolar", { description: (e as ApiError).message });
    }
  }

  async function handleReconciliarTodo(r: RouterSummary) {
    if (!confirm(
      `Reconciliar TODO el router "${r.nombre}"?\n\n` +
      `Se encola reconstrucción de: filter rule, profiles, secrets PPPoE y ` +
      `address-list de TODOS los contratos no-cancelados. Útil cuando el router ` +
      `se reemplazó por uno nuevo vacío.\n\n` +
      `Procesa en segundo plano; puedes seguir trabajando.`
    )) return;
    setReconcileTitle(`Reconciliar todo — ${r.nombre}`);
    try {
      const res = await reconciliarTodoMut.mutateAsync(r.id);
      setPollingJobId(res.job_id);
      toast.info("Reconciliación completa encolada", {
        description: "Procesando todos los contratos…",
      });
    } catch (e) {
      toast.error("Error al encolar", { description: (e as ApiError).message });
    }
  }

  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Routers</h2>
          <p className="text-xs text-muted-foreground">{data ? `${data.total} ${data.total === 1 ? "router" : "routers"}` : "Cargando…"}</p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}><Plus size={14} /> <span className="max-md:hidden">Nuevo router</span><span className="md:hidden">Nuevo</span></Button>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={searchInput} onChange={(e) => { setSearchInput(e.target.value); setPage(1); }} placeholder="Buscar por nombre o IP…" className="pl-8" />
        </div>
      </div>

      {isMobile ? (
        isLoading && items.length === 0 ? (
          <MobileEmpty>Cargando…</MobileEmpty>
        ) : items.length === 0 ? (
          <MobileEmpty>{search ? "Sin coincidencias." : "Registra el primer MikroTik."}</MobileEmpty>
        ) : (
          <MobileList>
            {items.map((r) => (
              <MobileCard
                key={r.id}
                onClick={() => setEditingId(r.id)}
                title={<span className="inline-flex items-center gap-1.5"><Circle size={10} className={ESTATUS_COLOR[r.estatus] + " fill-current"} />{r.nombre}</span>}
                subtitle={<><span className="font-mono">{r.ip_address}</span>{r.identity && r.identity !== r.nombre ? ` · ${r.identity}` : ""}</>}
                right={<Badge variant={r.activo ? "success" : "secondary"}>{r.activo ? "activo" : "inactivo"}</Badge>}
                actions={
                  <>
                    <Button variant="ghost" size="sm" disabled={recrearMut.isPending || reconciliarTodoMut.isPending} onClick={() => handleRecrearReglas(r)} title="Recrear reglas de firewall"><ShieldCheck size={13} /> Reglas</Button>
                    <Button variant="ghost" size="sm" disabled={recrearMut.isPending || reconciliarTodoMut.isPending} onClick={() => handleReconciliarTodo(r)} title="Reconciliar TODO"><DownloadCloud size={13} /> Reconciliar</Button>
                    <Button variant="ghost" size="sm" onClick={() => setEditingId(r.id)}><Pencil size={13} /> Editar</Button>
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(r)}><Trash2 size={13} /></Button>
                  </>
                }
              >
                <MobileMeta label="Modelo">{r.modelo ?? "—"}</MobileMeta>
                <MobileMeta label="RouterOS" mono>{r.version_ros ?? "—"}</MobileMeta>
                <MobileMeta label="Last seen" span>{r.last_seen ? formatDate(r.last_seen) : "—"}</MobileMeta>
              </MobileCard>
            ))}
          </MobileList>
        )
      ) : (
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Estado</TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead>IP</TableHead>
              <TableHead>Modelo</TableHead>
              <TableHead>RouterOS</TableHead>
              <TableHead>Last seen</TableHead>
              <TableHead>Activo</TableHead>
              <TableHead className="text-right pr-4">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 ? (
              <TableEmpty>Cargando…</TableEmpty>
            ) : items.length === 0 ? (
              <TableEmpty>
                <EmptyState icon={Router} title="Sin routers" description={search ? "Sin coincidencias." : "Registra el primer MikroTik."}
                  action={!search ? <Button size="sm" onClick={() => setCreateOpen(true)}><Plus size={14} /> Nuevo router</Button> : undefined} />
              </TableEmpty>
            ) : (
              items.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => setEditingId(r.id)}>
                  <TableCell>
                    <Circle size={10} className={ESTATUS_COLOR[r.estatus] + " fill-current"} />
                  </TableCell>
                  <TableCell className="font-medium">
                    {r.nombre}
                    {r.identity && r.identity !== r.nombre && (
                      <div className="text-[10px] text-muted-foreground">({r.identity})</div>
                    )}
                  </TableCell>
                  <TableCell className="font-mono">{r.ip_address}</TableCell>
                  <TableCell className="text-muted-foreground">{r.modelo ?? "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{r.version_ros ?? "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{r.last_seen ? formatDate(r.last_seen) : "—"}</TableCell>
                  <TableCell><Badge variant={r.activo ? "success" : "secondary"}>{r.activo ? "activo" : "inactivo"}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost" size="icon"
                        disabled={recrearMut.isPending || reconciliarTodoMut.isPending}
                        onClick={() => handleRecrearReglas(r)}
                        title="Recrear reglas de firewall (solo suspend-drop)"
                      >
                        <ShieldCheck size={14} />
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        disabled={recrearMut.isPending || reconciliarTodoMut.isPending}
                        onClick={() => handleReconciliarTodo(r)}
                        title="Reconciliar TODO (firewall + profiles + todos los contratos) — router reemplazado"
                      >
                        <DownloadCloud size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setEditingId(r.id)} title="Editar"><Pencil size={14} /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(r)} title="Eliminar"><Trash2 size={14} className="text-destructive" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs">
            <span className="text-muted-foreground">Página {data?.page ?? 1} de {totalPages}{isFetching && " · actualizando…"}</span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Anterior</Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
            </div>
          </div>
        )}
      </div>
      )}

      <RouterFormDialog open={createOpen} onOpenChange={setCreateOpen} router={null} />
      <RouterFormDialog open={!!editingId && !!editing} onOpenChange={(v) => { if (!v) setEditingId(null); }} router={editing ?? null} />

      <ReconcileResultDialog
        open={reconcileResult !== null}
        onOpenChange={(v) => { if (!v) setReconcileResult(null); }}
        result={reconcileResult}
        title={reconcileTitle}
      />
    </div>
  );
}
